import React, { useEffect, useState } from "react";
import { IonSpinner } from "@ionic/react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import API from "../../../services";
import { useTranslation } from "../../../hooks/useTranslation";
import { connect } from "react-redux";
import { RootState } from "../../../models/RootState";
import { getDynamicTextColors } from "../../../store/authActions";

interface ContentData {
  content: string;
  redirectionUrl: string;
  priority: number;
}

interface ContentResp {
  status: string;
  errorDescription: string;
  data: ContentData[];
}

interface ContentPageProps {
  userDetails?: any;
  tag: string;
  title: string;
}

const ContentPage: React.FC<ContentPageProps> = ({ userDetails, tag, title }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [contentData, setContentData] = useState<ContentData[]>([]);
  const navigate = useNavigate();

  // Get dynamic text color classes based on different backgrounds
  const { pageTextColorClass: textColorClass, inputBoxTextColorClass } = getDynamicTextColors(userDetails);

  const handleBackClick = () => {
    navigate(-1);
  };

  const getContentInfo = async () => {
    setLoading(true);

    let response;
    try {
      response = await API.post(`/webapp/get-content`, {
        tag: tag
      });
    } catch (e) {
      console.log(e);
      setLoading(false);
      return;
    }

    if (response.data.status !== "RS_OK") {
      navigate("/home");
    }

    if (response && response.data?.data) {
      setContentData(response.data.data);
    }
    setLoading(false);
  };

  const handleContentClick = (redirectionURL: string) => {
    if (redirectionURL && redirectionURL.trim() !== "") {
      navigate(redirectionURL);
    }
  };

  const renderContent = () => {
    if (!contentData || contentData.length === 0) {
      return <div className={textColorClass === 'dark-text' ? 'text-black' : 'text-white'}>{t("profile.noFaqContentAvailable")}</div>;
    }

    return contentData.map((item, index) => (
      <div 
        key={index} 
        className={`mb-2 hover:bg-opacity-80 transition-all ${textColorClass}`}
        onClick={() => handleContentClick(item.redirectionUrl)}
        style={{ cursor: item.redirectionUrl && item.redirectionUrl.trim() !== "" ? 'pointer' : 'default' }}
      >
        <div 
          className=""
          dangerouslySetInnerHTML={{ __html: item.content }}
        />
      </div>
    ));
  };

  useEffect(() => {
    getContentInfo();
  }, [tag]);

  return (
    <>
      <div className={`flex flex-col px-3 mt-4 w-full pb-[7rem] py-[4rem] content-page ${textColorClass}`}>
        <header className={`flex gap-3 items-center self-start text-base font-medium text-center mb-[0.5rem] ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}>
        <div>
            <div className="bg-inputbox-color flex p-1 mr-[0.25rem] rounded"
                onClick={() => handleBackClick()}
            >
                <ArrowBackIcon
                    className={`aspect-square w-[18px] ${inputBoxTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
                    style={{ width: '20px', height: '20px' }}
                />
            </div>
        </div>
        <h1 className="text-[1.2rem] font-medium">{title}</h1>
        </header>
        <div className="mt-2">
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <IonSpinner name="crescent" />
            </div>
          ) : (
            renderContent()
          )}
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    userDetails: state.userDetails?.userDetails,
  };
};

export default connect(mapStateToProps)(ContentPage);
